<template>
  <div class="relative flex items-center" data-testid="priorityPlusWrapper">
    <nav class="grid w-full grid-cols-1 grid-rows-1 items-center">
      <!--    The shadow component for observing visibility of elements -->
      <ul
        ref="root"
        class="pointer-events-none invisible col-span-full row-span-full"
        :class="
          cn(
            'flex flex-nowrap items-center justify-between gap-x-2 overflow-hidden',
            listWrapperClass,
          )
        ">
        <li
          v-for="(item, index) in items"
          :key="`${item.id}_${index}`"
          v-intersection-observer="[
            onIntersectionObserver,
            {
              root,
              threshold: 1.0,
              rootMargin: '0px 0px 0px 0px',
              ...intersectionObserverOptions,
            },
          ]"
          :class="listItemClass"
          :data-id="`${item.id}_${index}`">
          <slot name="item" :item="item" />
        </li>
      </ul>
      <!--    The actual navigation elements-->
      <ul
        class="col-span-full row-span-full"
        :class="
          cn(
            'flex flex-nowrap items-center justify-between gap-x-2 overflow-hidden',
            listWrapperClass,
          )
        ">
        <li v-for="item in visibleItems" :key="item.id" :class="listItemClass">
          <slot name="item" :item="item" />
        </li>
      </ul>
    </nav>
    <!--    The plus button with all the invisible navigation elements -->
    <Popover v-if="invisibleItems.length" :has-close-icon="false">
      <template #trigger="{ togglePopover, isOpen }">
        <UiButton
          data-testid="moreMenuButton"
          class="sticky right-0"
          :class="moreButtonClass"
          :appearance="isOpen ? 'fill-emphasis' : 'fill-subtle'"
          size="sm"
          @click="
            handleMoreButtonClick();
            togglePopover();
          ">
          +{{ invisibleItems.length }}
        </UiButton>
      </template>
      <template #content="{ closePopover }">
        <slot
          name="plusItems"
          :invisible-items="invisibleItems"
          :close-menu="closePopover" />
      </template>
    </Popover>
  </div>
</template>

<script
  setup
  lang="ts"
  generic="
    T extends
      | TopLevelNavigationItem
      | { urn: string; id: string; versionCreated: string; label: string }
      | Record<any, any>
  ">
import UiButton from "@dpa-id-components/dpa-shared-components/src/components/UiButton/UiButton.vue";
import { cn } from "@dpa-id-components/dpa-shared-components/src/utils/cn.ts";
import { vIntersectionObserver } from "@vueuse/components";
import {
  useDebounceFn,
  type UseIntersectionObserverOptions,
} from "@vueuse/core";
import { computed, type Ref, ref, useTemplateRef } from "vue";

import Popover from "@/components/shared/Popover/Popover.vue";
import { useTracking } from "@/composition/shared/useTracking.ts";
import type { TopLevelNavigationItem } from "@/types/Cvd.ts";

const root = useTemplateRef<HTMLUListElement>("root");
const {
  items,
  intersectionObserverOptions = {
    threshold: 1.0,
    rootMargin: "0px 0px 0px 0px",
  },
  moreButtonClass = "ml-2",
  listWrapperClass = "",
  listItemClass = "",
} = defineProps<{
  items: T[];
  moreButtonClass?: string;
  listWrapperClass?: string;
  listItemClass?: string;
  intersectionObserverOptions?: Pick<
    UseIntersectionObserverOptions,
    "threshold" | "rootMargin" | "immediate"
  >;
}>();

const invisibleItems: Ref<T[]> = ref([]);

const visibleItems = computed(() =>
  items.filter((item) => !invisibleItems.value.includes(item)),
);

async function onIntersectionObserver([entry]: IntersectionObserverEntry[]) {
  await useDebounceFn(() => {
    // @ts-ignore  - Intersection Observer target is an Element not HTMLelement so it errors here but it's available in this case
    const targetCompositeId = entry.target.dataset.id;
    if (!targetCompositeId) return;

    // parse composite "id_index"
    const parts = targetCompositeId.split("_");
    const originalId = parts.slice(0, -1).join("_");
    const itemIndex = parseInt(parts[parts.length - 1], 10);

    // find item by id and index
    const item: T | undefined = items.find(
      (item, idx) => item.id === originalId && idx === itemIndex,
    );
    if (!item) return;

    if (!entry.isIntersecting) {
      if (!invisibleItems.value.includes(item)) {
        invisibleItems.value.push(item);
        // sort to preserve original order
        invisibleItems.value.sort(
          (a, b) => items.indexOf(a) - items.indexOf(b),
        );
      }
      return;
    }

    if (entry.isIntersecting) {
      // remove from invisibleItems
      const idx = invisibleItems.value.indexOf(item);
      if (idx !== -1) invisibleItems.value.splice(idx, 1);
      return;
    }
  }, 100)();
}

const { trackEvent } = useTracking();
function handleMoreButtonClick() {
  trackEvent("categoryNavigation_click_iconButton_showMoreNavigation");
}
</script>
